import React, { useEffect, useState } from "react";
import { DollarSign, Plus, X, Pencil } from "lucide-react";
import { listPricing, createPricing, updatePricing } from "../../services/pricingService";
import "../../assets/css/ProjectList.css";

const MODEL_TYPES = ["llm", "embedding", "tts", "stt"];
const PRICING_UNITS = ["per_million_tokens", "per_minute"];

const emptyForm = {
  modelName: "",
  modelType: "llm",
  pricingUnit: "per_million_tokens",
  inputPricePerMillion: "",
  outputPricePerMillion: "",
  pricePerUnit: "",
  isActive: 1,
};

const normalizeRow = (row) => ({
  modelPid: row.modelPid ?? row.model_pid,
  modelName: row.modelName ?? row.model_name,
  modelType: row.modelType ?? row.model_type,
  pricingUnit: row.pricingUnit ?? row.pricing_unit,
  inputPricePerMillion: row.inputPricePerMillion ?? row.input_price_per_million,
  outputPricePerMillion: row.outputPricePerMillion ?? row.output_price_per_million,
  pricePerUnit: row.pricePerUnit ?? row.price_per_unit,
  isActive: row.isActive ?? row.is_active,
});

const AiModelPricing = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingPid, setEditingPid] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchPricing = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await listPricing();
      const list = Array.isArray(res.data?.response) ? res.data.response : [];
      setRows(list.map(normalizeRow));
    } catch {
      setError("Failed to load pricing.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  const openCreateModal = () => {
    setEditingPid(null);
    setForm(emptyForm);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditingPid(row.modelPid);
    setForm({
      modelName: row.modelName ?? "",
      modelType: row.modelType ?? "llm",
      pricingUnit: row.pricingUnit ?? "per_million_tokens",
      inputPricePerMillion: row.inputPricePerMillion ?? "",
      outputPricePerMillion: row.outputPricePerMillion ?? "",
      pricePerUnit: row.pricePerUnit ?? "",
      isActive: row.isActive ?? 1,
    });
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingPid(null);
  };

  const handleSave = async () => {
    setFormError("");
    if (!form.modelName.trim()) {
      setFormError("Model name is required.");
      return;
    }
    if (form.pricingUnit === "per_minute" && form.pricePerUnit === "") {
      setFormError("Price per unit is required for per-minute pricing.");
      return;
    }

    setSaving(true);
    try {
      if (editingPid) {
        const payload = {
          modelName: form.modelName,
          isActive: form.isActive ? 1 : 0,
        };
        if (form.pricingUnit === "per_million_tokens") {
          payload.inputPricePerMillion = form.inputPricePerMillion === "" ? 0 : Number(form.inputPricePerMillion);
          payload.outputPricePerMillion = form.outputPricePerMillion === "" ? 0 : Number(form.outputPricePerMillion);
        } else {
          payload.pricePerUnit = Number(form.pricePerUnit);
        }
        await updatePricing(editingPid, payload);
      } else {
        const payload = {
          modelName: form.modelName,
          modelType: form.modelType,
          pricingUnit: form.pricingUnit,
        };
        if (form.pricingUnit === "per_million_tokens") {
          payload.inputPricePerMillion = form.inputPricePerMillion === "" ? 0 : Number(form.inputPricePerMillion);
          payload.outputPricePerMillion = form.outputPricePerMillion === "" ? 0 : Number(form.outputPricePerMillion);
        } else {
          payload.pricePerUnit = Number(form.pricePerUnit);
        }
        await createPricing(payload);
      }
      setShowModal(false);
      setEditingPid(null);
      await fetchPricing();
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to save pricing.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">
        <section className="url-list-toolbar p-3 mb-3">
          <div className="url-list-actions">
            <div>
              <h5 style={{ margin: 0, fontWeight: 700 }}>AI Model Pricing</h5>
              <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)" }}>
                Manage pricing for LLM, embedding, TTS and STT models
              </p>
            </div>

            <button className="btn btn-primary url-settings-btn" onClick={openCreateModal}>
              <Plus size={16} className="me-2" />
              Add Model Pricing
            </button>
          </div>
        </section>

        <section className="url-table-card">
          <div className="table-responsive">
            <table className="table url-table table-hover align-middle mb-0">
              <colgroup>
                <col style={{ width: "20%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Model Name</th>
                  <th>Type</th>
                  <th>Pricing Unit</th>
                  <th>Input Price / 1M</th>
                  <th>Output Price / 1M</th>
                  <th>Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="text-center text-muted py-5">Loading...</td></tr>
                ) : error ? (
                  <tr><td colSpan="7" className="text-center text-danger py-5">{error}</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="7" className="text-center text-muted py-5">No pricing entries found.</td></tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.modelPid}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                            background: "var(--primaryTint)", color: "var(--primaryColor)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <DollarSign size={15} />
                          </div>
                          <span className="url-page-title">{row.modelName}</span>
                        </div>
                      </td>
                      <td><span className="ph-session-badge">{row.modelType}</span></td>
                      <td>{row.pricingUnit === "per_minute" ? "Per Minute" : "Per Million Tokens"}</td>
                      <td>
                        {row.pricingUnit === "per_minute"
                          ? `$${row.pricePerUnit ?? 0}`
                          : `$${row.inputPricePerMillion ?? 0}`}
                      </td>
                      <td>
                        {row.pricingUnit === "per_minute" ? "—" : `$${row.outputPricePerMillion ?? 0}`}
                      </td>
                      <td>
                        <span
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            fontSize: 11, fontWeight: 700,
                            padding: "4px 10px", borderRadius: 999,
                            background: row.isActive ? "#f0fdf4" : "#fff1f2",
                            color: row.isActive ? "#166534" : "#dc2626",
                          }}
                        >
                          {row.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-center">
                        <Pencil
                          size={16}
                          className="cursor-pointer"
                          style={{ color: "var(--primaryColor)" }}
                          onClick={() => openEditModal(row)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {showModal && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={closeModal}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480, boxShadow: "0 8px 32px rgba(15,23,42,0.18)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--borderColor)" }}>
              <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
                {editingPid ? "Edit Model Pricing" : "Add Model Pricing"}
              </h6>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }} onClick={closeModal}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>
              {formError && (
                <div className="alert alert-danger py-2 px-3 mb-0" style={{ fontSize: 13 }}>{formError}</div>
              )}

              <div>
                <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Model Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.modelName}
                  onChange={(e) => setForm((f) => ({ ...f, modelName: e.target.value }))}
                  placeholder="e.g. gpt-4o-mini"
                />
              </div>

              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Model Type</label>
                  <select
                    className="form-select"
                    value={form.modelType}
                    disabled={!!editingPid}
                    onChange={(e) => setForm((f) => ({ ...f, modelType: e.target.value }))}
                  >
                    {MODEL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Pricing Unit</label>
                  <select
                    className="form-select"
                    value={form.pricingUnit}
                    disabled={!!editingPid}
                    onChange={(e) => setForm((f) => ({ ...f, pricingUnit: e.target.value }))}
                  >
                    {PRICING_UNITS.map((u) => <option key={u} value={u}>{u === "per_minute" ? "Per Minute" : "Per Million Tokens"}</option>)}
                  </select>
                </div>
              </div>

              {form.pricingUnit === "per_million_tokens" ? (
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Input Price / 1M</label>
                    <input
                      type="number"
                      step="0.0001"
                      className="form-control"
                      value={form.inputPricePerMillion}
                      onChange={(e) => setForm((f) => ({ ...f, inputPricePerMillion: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Output Price / 1M</label>
                    <input
                      type="number"
                      step="0.0001"
                      className="form-control"
                      value={form.outputPricePerMillion}
                      onChange={(e) => setForm((f) => ({ ...f, outputPricePerMillion: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Price Per Minute (USD)</label>
                  <input
                    type="number"
                    step="0.0001"
                    className="form-control"
                    value={form.pricePerUnit}
                    onChange={(e) => setForm((f) => ({ ...f, pricePerUnit: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
              )}

              {editingPid && (
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isActiveCheck"
                    checked={!!form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked ? 1 : 0 }))}
                  />
                  <label className="form-check-label" htmlFor="isActiveCheck" style={{ fontSize: 13 }}>Active</label>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "0 20px 20px" }}>
              <button className="btn btn-light" style={{ fontSize: 13, borderRadius: 8 }} onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" style={{ fontSize: 13, borderRadius: 8, fontWeight: 600 }} onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : editingPid ? "Save Changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AiModelPricing;
